<template>
  <!-- Явный dark:bg-gray-800 вместо bg-default/--ui-bg: этот враппер занимает весь
       экран (h-dvh) и физически перекрывает body, поэтому его фон — то, что видно
       как "фон страницы". bg-default отслеживает CSS-переменную --ui-bg из
       main.css — но переопределения --ui-bg там (через .dark{}/html.dark{}) на
       практике НЕ применяются: сборка Nuxt UI схлопывает/переписывает повторные
       правила на .dark/--ui-bg (проверено через CSSOM в браузере — второе правило
       либо пропадает, либо его значение подменяется на color-mix(...) самим
       движком темы), так что bg-default тут всегда откатывался на дефолт
       neutral-900. Хардкодим явный класс в обход этого механизма — по просьбе
       пользователя 2026-08-21 фон страницы = тот же цвет, что раньше был на
       карточках тендеров (gray-800, #1e2939); карточки (TendersPage.vue) при
       этом вернули на исходный dark:bg-gray-900 (#101828) — так оба цвета снова
       различимы, только в другую сторону (фон светлее, карточки темнее).
       Светлая тема (#F9FAFC → #F6F7FA, 2026-09-10) тем же механизмом не страдает —
       html:not(.dark) в main.css применяется штатно — но раз уж этот класс здесь
       захардкожен явно, держите его значение синхронным с main.css вручную. -->
  <UDashboardGroup class="flex-col lg:flex-row h-dvh bg-[#F4F7FB] dark:bg-gray-800"
    @touchstart="onTouchStart"
    @touchmove="onTouchMove"
    @touchend="onTouchEnd"
  >
    <!-- Пропустить навигацию (Vercel WIG, 2026-10-10): видна только при фокусе с клавиатуры -->
    <a href="#main-content"
      class="sr-only focus-visible:not-sr-only focus-visible:fixed focus-visible:top-2 focus-visible:left-2 focus-visible:z-[100] focus-visible:rounded-lg focus-visible:bg-primary-600 focus-visible:px-4 focus-visible:py-2 focus-visible:text-sm focus-visible:font-medium focus-visible:text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
    >Перейти к содержимому</a>
    <!-- Mobile header -->
    <header v-if="!hideMobileHeader" class="header-shadow lg:hidden h-14 bg-white dark:bg-gray-900 border-b border-default dark:border-gray-800 px-4 flex items-center gap-2 sticky top-0 z-20 flex-shrink-0">
      <!-- «Назад» на вложенных страницах, иначе — меню (единственный вход в меню на телефоне:
           нижнюю панель разделов убрали 2026-10-20). -->
      <UButton v-if="headerBackBtn" icon="lucide:arrow-left" color="neutral" variant="ghost" size="md" aria-label="Назад"
        class="shrink-0 size-9 justify-center -ml-2" @click="headerBackBtn.action()" />
      <UButton v-else icon="lucide:menu" color="neutral" variant="ghost" size="md" aria-label="Меню"
        :aria-expanded="sidebarOpen" class="shrink-0 size-9 justify-center -ml-2" @click="openSidebar()" />
      <div class="flex-1 flex items-center h-9 min-w-0">
        <h1 class="text-base font-semibold text-gray-900 dark:text-white truncate">{{ pageTitle }}</h1>
      </div>
      <!-- Header action button -->
      <UButton
        v-if="headerActionBtn"
        :icon="headerActionBtn.icon"
        color="neutral"
        variant="ghost"
        size="md"
        :title="headerActionBtn.title"
        :aria-label="headerActionBtn.title"
        class="shrink-0 size-9 justify-center"
        :ui="{ leadingIcon: headerActionBtn.spinning ? 'animate-spin' : '' }"
        @click="headerActionBtn.action()"
      />
      <!-- Three-dot menu -->
      <div class="relative shrink-0">
        <UButton
          v-if="menuItems.length > 0"
          icon="lucide:ellipsis-vertical"
          color="neutral"
          variant="ghost"
          size="md"
          aria-label="Ещё"
          :aria-expanded="showHeaderMenu"
          class="size-9 justify-center -mr-2"
          @click="showHeaderMenu = !showHeaderMenu"
        />
        <div v-else-if="!headerActionBtn" class="w-9" />

        <!-- Popover menu -->
        <Transition
          enter-active-class="transition duration-150"
          enter-from-class="opacity-0 scale-95"
          enter-to-class="opacity-100 scale-100"
          leave-active-class="transition duration-100"
          leave-from-class="opacity-100 scale-100"
          leave-to-class="opacity-0 scale-95"
        >
          <div
            v-if="showHeaderMenu"
            class="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-900 rounded-xl shadow-lg border border-default dark:border-gray-700 py-1 z-50 origin-top-right"
          >
            <button
              v-for="item in menuItems"
              :key="item.id"
              @click="item.action(); showHeaderMenu = false"
              class="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
            >
              <Icon :name="item.icon" class="w-4 h-4 text-gray-400" />
              {{ item.label }}
            </button>
          </div>
        </Transition>
      </div>
    </header>

    <!-- Backdrop: header menu -->
    <div v-if="showHeaderMenu" class="fixed inset-0 z-10 lg:hidden" @click="showHeaderMenu = false" />

    <!-- Mobile sidebar backdrop/drawer is handled internally by UDashboardSidebar (USlideover) -->
    <LayoutSidebar />
    <UDashboardSearch :groups="searchGroups" />
    <main
      id="main-content"
      tabindex="-1"
      class="flex-1 min-h-0 overflow-x-hidden overflow-y-auto lg:w-0 focus:outline-none pb-[env(safe-area-inset-bottom)] lg:pb-0"
      :class="noMainScroll ? 'lg:overflow-y-hidden' : 'lg:overflow-y-auto'"
    >
      <slot />
    </main>

    <!-- Резервирует место под полноразмерный чат (ChatFab, см. app.vue), чтобы
         он вёл себя как сайдбар и сжимал остальной контент, а не перекрывал его. -->
    <div
      class="hidden flex-none lg:block"
      :class="{ 'transition-[width] duration-150': !chatIsResizing }"
      :style="{ width: chatShouldReserveSpace ? chatWidth + 'px' : '0px' }"
    />

    <!-- Mobile FAB -->
    <UButton
      v-if="fabAction"
      :icon="fabAction.icon"
      :aria-label="fabAction.label"
      size="xl"
      class="lg:hidden fixed bottom-[calc(1.25rem+env(safe-area-inset-bottom))] right-5 z-20 size-14 justify-center rounded-full shadow-lg shadow-primary-500/30 active:scale-95 transition-transform"
      :ui="{ leadingIcon: 'size-6' }"
      @click="fabAction.action()"
    />
  </UDashboardGroup>
</template>

<script setup lang="ts">
const { isOpen: sidebarOpen, open: openSidebar, close: closeSidebar } = useSidebar()
const { items: menuItems, fab: fabAction, headerAction: headerActionBtn, backAction: headerBackBtn, hideMobileHeader, noMainScroll } = useHeaderMenu()
const showHeaderMenu = ref(false)
const pageTitle = usePageTitle()

const { navigation } = useMainNavigation()
const searchGroups = computed(() => [{
  id: 'links',
  label: 'Перейти',
  items: navigation.value.flatMap(item => [
    { label: item.name, icon: item.icon, to: item.href },
    ...(item.children?.map(child => ({ label: child.name, icon: child.icon, to: child.href })) || []),
  ]),
}])

// Полноразмерный режим чата (ChatFab) ведёт себя как сайдбар — сжимает эту
// область вместо перекрытия. Стейт общий (useChatFabState), лишних побочных
// эффектов виджета здесь не заводим.
const { isOpen: chatIsOpen, isExpanded: chatIsExpanded, chatWidth, isResizing: chatIsResizing } = useChatFabState()
const chatShouldReserveSpace = computed(() => chatIsOpen.value && chatIsExpanded.value)

// Close menu on route change
const route = useRoute()
watch(() => route.fullPath, () => {
  showHeaderMenu.value = false
})

// Swipe gesture to open/close sidebar on mobile
const touchStartX = ref(0)
const touchStartY = ref(0)
const isSwiping = ref(false)
const EDGE_ZONE = 30 // pixels from left edge to start swipe
const SWIPE_THRESHOLD = 60 // min distance to trigger open/close

function onTouchStart(e: TouchEvent) {
  if (window.innerWidth >= 1024) return // lg breakpoint — desktop, skip
  const touch = e.touches[0]
  touchStartX.value = touch.clientX
  touchStartY.value = touch.clientY

  // Only start tracking if touch begins near left edge (open) or sidebar is open (close)
  if (touch.clientX <= EDGE_ZONE || sidebarOpen.value) {
    isSwiping.value = true
  }
}

function onTouchMove(e: TouchEvent) {
  if (!isSwiping.value) return
  const touch = e.touches[0]
  const deltaY = Math.abs(touch.clientY - touchStartY.value)
  const deltaX = Math.abs(touch.clientX - touchStartX.value)

  // If vertical scroll is dominant, cancel swipe
  if (deltaY > deltaX) {
    isSwiping.value = false
  }
}

function onTouchEnd(e: TouchEvent) {
  if (!isSwiping.value) return
  isSwiping.value = false

  const touch = e.changedTouches[0]
  const deltaX = touch.clientX - touchStartX.value

  if (!sidebarOpen.value && touchStartX.value <= EDGE_ZONE && deltaX > SWIPE_THRESHOLD) {
    // Swipe right from left edge → open
    openSidebar()
  } else if (sidebarOpen.value && deltaX < -SWIPE_THRESHOLD) {
    // Swipe left while open → close
    closeSidebar()
  }
}
</script>
